import { type ReactElement } from 'react';
import { Bar, BarChart, Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { DashboardWidget } from '../../../features/dashboards/index.js';

/**
 * Preview estático de um widget. MVP do dashboard builder — os
 * renderers reais (que executam a query referenciada em `query_ref`)
 * entram em M1 junto com o motor de execução (via `db-analyst` ou
 * RPC dedicada). Aqui mostramos apenas um placeholder visual
 * compatível com o tipo do widget.
 *
 * Regras:
 *  - KPI tile → número grande + trend mock
 *  - line/area → linha sintética de 10 pontos aleatórios mas estáveis
 *    (seeded pelo id do widget para preview reprodutível em re-render)
 *  - bar → 5 barras sintéticas
 *  - pie/table/narrative/ai_card → placeholder textual
 */
export function WidgetPreview({ widget }: { widget: DashboardWidget }): ReactElement {
  switch (widget.widget_type) {
    case 'kpi':
      return <KpiPreview widgetId={widget.id} />;
    case 'line':
    case 'area':
      return <LinePreview widgetId={widget.id} />;
    case 'bar':
      return <BarPreview widgetId={widget.id} />;
    default:
      return <Placeholder type={widget.widget_type} />;
  }
}

function KpiPreview({ widgetId }: { widgetId: string }) {
  // Mock estável a partir do id — mesmo número em cada render.
  const n = seededNumber(widgetId, 1_000, 99_999);
  const trend = seededNumber(widgetId + 'trend', -15, 25);
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-accent">Preview KPI</p>
      <p className="mt-2 text-3xl font-bold tracking-tight">
        {new Intl.NumberFormat('pt-BR').format(n)}
      </p>
      <p className={`mt-1 text-sm ${trend >= 0 ? 'text-success' : 'text-destructive'}`}>
        {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}% vs período anterior
      </p>
    </div>
  );
}

function LinePreview({ widgetId }: { widgetId: string }) {
  const data = generateSeries(widgetId, 10);
  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke="hsl(var(--accent))"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BarPreview({ widgetId }: { widgetId: string }) {
  const data = generateSeries(widgetId, 5);
  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <Tooltip />
          <Bar dataKey="value" fill="hsl(var(--primary))" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Placeholder({ type }: { type: string }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
      Preview de <span className="mx-1 font-mono">{type}</span> em M1 (motor de queries).
    </div>
  );
}

// Mock estável: mesmo widget.id → mesmos valores em cada render.
function seededNumber(seed: string, min: number, max: number): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  const x = Math.abs(h % 10_000) / 10_000;
  return Math.floor(min + x * (max - min));
}

function generateSeries(seed: string, n: number): { value: number }[] {
  return Array.from({ length: n }, (_, i) => ({
    value: seededNumber(seed + i, 20, 80),
  }));
}
